import { Link } from 'react-router-dom';
import { ArrowUpRight, Linkedin, Instagram, Facebook, ArrowUp } from 'lucide-react';
import { STATIC, subscribeNewsletter } from '@/lib/cms';
import { useSettings } from '@/lib/settings';
import { useState } from 'react';
import { toast } from 'sonner';
import BrandMark from './BrandMark';

export default function Footer() {
  const settings = useSettings();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    const { ok } = await subscribeNewsletter(email);
    setLoading(false);
    if (ok) { toast.success('You\'re on the list. Welcome to Slinkflow.'); setEmail(''); }
    else toast.error('Couldn\'t subscribe right now. Try again.');
  };

  return (
    <footer className="relative overflow-hidden bg-ink-50" data-testid="site-footer">
      <div className="absolute -top-32 right-0 h-[420px] w-[420px] hero-gradient-orb opacity-50" />
      <div className="relative mx-auto max-w-[1400px] px-6 py-24 md:px-10 lg:py-32">
        <div className="grid items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <p className="mb-6 text-xs uppercase tracking-[0.3em] text-ink-400">Let&apos;s build</p>
            <h2 className="font-display text-[44px] font-light leading-[1.05] tracking-tight text-ink-900 md:text-[72px] lg:text-[92px]">
              The future of your <span className="gradient-text font-medium">brand</span>, starts now.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/contact" data-testid="footer-cta" className="group inline-flex items-center gap-3 rounded-full bg-ink-900 px-7 py-5 text-white transition-transform hover:scale-[1.02]">
              <span className="font-display text-lg">Start a project</span>
              <span className="grid h-9 w-9 place-items-center rounded-full bg-cyan transition-transform group-hover:rotate-45">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>

        <div className="my-16 divider-grad" />

        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <BrandMark size="md" />
            <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-ink-500">
              {settings.tagline}
            </p>
            <div className="mt-5 space-y-1 text-sm text-ink-500">
              <p><a className="hover:text-cyan" href={`mailto:${settings.email}`}>{settings.email}</a></p>
              {settings.phone && <p><a className="hover:text-cyan" href={`tel:${settings.phone.replace(/[^+\d]/g,'')}`}>{settings.phone}</a>{settings.phone_secondary ? ` · ${settings.phone_secondary}` : ''}</p>}
            </div>
            <form onSubmit={onSubscribe} className="mt-8 flex max-w-md items-center gap-2 rounded-full border border-ink-100 bg-white p-1.5 pl-5">
              <input
                data-testid="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 bg-transparent text-[14px] text-ink-900 placeholder:text-ink-300 focus:outline-none"
              />
              <button data-testid="newsletter-submit" disabled={loading} className="btn-cyan rounded-full px-5 py-2.5 text-sm font-medium">
                {loading ? '...' : 'Subscribe'}
              </button>
            </form>
          </div>

          <FooterCol title="Services" items={STATIC.services.map(s => ({ to: `/services/${s.slug}`, label: s.title }))} />
          <FooterCol title="Company" items={[
            { to: '/about', label: 'About' },
            { to: '/portfolio', label: 'Work' },
            { to: '/blog', label: 'Journal' },
            { to: '/contact', label: 'Contact' },
          ]} />
          <FooterCol title="Resources" items={[
            // Pricing hidden for now — uncomment to restore.
            // { to: '/pricing', label: 'Pricing' },
            // { to: '/testimonials', label: 'Testimonials' }, // disabled until real testimonials exist
            { to: '/contact', label: 'FAQ' },
            { to: '/privacy', label: 'Privacy' },
          ]} />
        </div>

        <div className="my-16 divider-grad" />

        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <p className="text-sm text-ink-400">© {new Date().getFullYear()} Slinkflow. All rights reserved.</p>
          <div className="flex items-center gap-2">
            <SocialIcon href={settings.social?.linkedin} label="LinkedIn"><Linkedin className="h-4 w-4" /></SocialIcon>
            <SocialIcon href={settings.social?.instagram} label="Instagram"><Instagram className="h-4 w-4" /></SocialIcon>
            <SocialIcon href={settings.social?.facebook} label="Facebook"><Facebook className="h-4 w-4" /></SocialIcon>
          </div>
          <button data-testid="back-to-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="group inline-flex items-center gap-2 text-sm text-ink-500 transition-colors hover:text-cyan">
            <span>Back to top</span>
            <span className="grid h-9 w-9 place-items-center rounded-full bg-white shadow-soft transition-transform group-hover:-translate-y-0.5">
              <ArrowUp className="h-4 w-4" />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <p className="mb-5 text-xs uppercase tracking-[0.25em] text-ink-400">{title}</p>
      <ul className="space-y-3">
        {items.map(i => (
          <li key={i.to}>
            <Link to={i.to} className="text-[15px] text-ink-700 link-underline hover:text-cyan">{i.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialIcon({ href, label, children }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" aria-label={label} data-testid={`social-${label.toLowerCase()}`} className="grid h-10 w-10 place-items-center rounded-full border border-ink-100 bg-white text-ink-500 transition-colors hover:border-cyan hover:text-cyan">
      {children}
    </a>
  );
}
